import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Image as ImageIcon, RotateCcw, Trash2, Check, Palette, Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import './CustomizePage.css';

const MAX_BG_DIMENSION = 1600;          // px — longest side after downscale
const BG_JPEG_QUALITY = 0.78;
const MAX_INPUT_FILE_BYTES = 12 * 1024 * 1024; // hard cap on raw upload (12 MB)

// Reads an image file, downscales it to MAX_BG_DIMENSION on its longest side
// and re-encodes as a JPEG data URL so the result fits in localStorage (~5 MB).
const readAndDownscaleImage = (file) =>
  new Promise((resolve, reject) => {
    if (!file) return reject(new Error('no_file'));
    if (!file.type.startsWith('image/')) return reject(new Error('not_image'));
    if (file.size > MAX_INPUT_FILE_BYTES) return reject(new Error('too_big'));

    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read_failed'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('decode_failed'));
      img.onload = () => {
        const { naturalWidth: w, naturalHeight: h } = img;
        const scale = Math.min(1, MAX_BG_DIMENSION / Math.max(w, h));
        const tw = Math.round(w * scale);
        const th = Math.round(h * scale);
        const canvas = document.createElement('canvas');
        canvas.width = tw;
        canvas.height = th;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, tw, th);
        try {
          resolve(canvas.toDataURL('image/jpeg', BG_JPEG_QUALITY));
        } catch (err) {
          reject(err);
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const CustomizePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme, updateTheme, reset, presets, quotaError } = useTheme();
  const fileInputRef = useRef(null);
  const [imgError, setImgError] = useState(null);
  const [busy, setBusy] = useState(false);

  const activePresetId = presets.find(
    (p) => p.color.toLowerCase() === (theme.primary || '').toLowerCase()
  )?.id;

  const handleSelectPreset = useCallback((color) => {
    updateTheme({ primary: color });
  }, [updateTheme]);

  const handleCustomColor = useCallback((e) => {
    updateTheme({ primary: e.target.value });
  }, [updateTheme]);

  const handleImageChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow picking the same file twice
    if (!file) return;
    setBusy(true);
    setImgError(null);
    try {
      const dataUrl = await readAndDownscaleImage(file);
      updateTheme({ backgroundImage: dataUrl });
    } catch (err) {
      setImgError(err.message === 'too_big' ? 'too_big' : 'generic');
    } finally {
      setBusy(false);
    }
  }, [updateTheme]);

  const handleRemoveImage = useCallback(() => {
    updateTheme({ backgroundImage: null });
    setImgError(null);
  }, [updateTheme]);

  const handleOpacityChange = useCallback((e) => {
    const value = Math.max(0, Math.min(1, parseFloat(e.target.value)));
    updateTheme({ backgroundOpacity: value });
  }, [updateTheme]);

  return (
    <div className="page-container animate-fade-in" data-testid="customize-page">
      <button className="workout-page-back" onClick={() => navigate(-1)}>
        <ChevronLeft size={14} />
        {t('common.back')}
      </button>

      <div className="header">
        <h1 className="header-title">{t('customize.title')}</h1>
        <Palette size={28} color="var(--primary)" />
      </div>
      <p className="customize-subtitle">{t('customize.subtitle')}</p>

      {/* ── Palette ──────────────────────────────────────────── */}
      <section className="customize-section">
        <h2 className="customize-section-title">{t('customize.palette_title')}</h2>
        <p className="customize-section-desc">{t('customize.palette_desc')}</p>

        <div className="palette-grid">
          {presets.map((preset) => {
            const isActive = activePresetId === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                className={`palette-swatch${isActive ? ' is-active' : ''}`}
                style={{ '--swatch': preset.color }}
                onClick={() => handleSelectPreset(preset.color)}
                aria-pressed={isActive}
                aria-label={t(preset.nameKey)}
              >
                <span className="palette-swatch-dot" />
                <span className="palette-swatch-name">{t(preset.nameKey)}</span>
                {isActive && <Check size={14} className="palette-swatch-check" />}
              </button>
            );
          })}
        </div>

        <label className="custom-color-row">
          <span className="custom-color-label">{t('customize.custom_color')}</span>
          <span className="custom-color-input-wrap">
            <input
              type="color"
              value={theme.primary}
              onChange={handleCustomColor}
              aria-label={t('customize.custom_color')}
            />
            <span className="custom-color-hex">{(theme.primary || '').toUpperCase()}</span>
          </span>
        </label>
      </section>

      {/* ── Background image ─────────────────────────────────── */}
      <section className="customize-section">
        <h2 className="customize-section-title">{t('customize.bg_title')}</h2>
        <p className="customize-section-desc">{t('customize.bg_desc')}</p>

        {theme.backgroundImage ? (
          <div className="bg-image-current">
            <img src={theme.backgroundImage} alt={t('customize.bg_preview_alt')} />
            <div className="bg-image-actions">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
              >
                <ImageIcon size={14} />
                {t('customize.bg_change')}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-remove-bg"
                onClick={handleRemoveImage}
                disabled={busy}
              >
                <Trash2 size={14} />
                {t('customize.bg_remove')}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="bg-image-upload"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            <ImageIcon size={28} />
            <span>{busy ? t('customize.bg_processing') : t('customize.bg_pick')}</span>
            <small>{t('customize.bg_hint')}</small>
          </button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleImageChange}
        />

        {imgError === 'too_big' && (
          <p className="customize-error">{t('customize.error_too_big')}</p>
        )}
        {imgError === 'generic' && (
          <p className="customize-error">{t('customize.error_image')}</p>
        )}
        {quotaError && (
          <p className="customize-error">{t('customize.error_quota')}</p>
        )}

        {theme.backgroundImage && (
          <label className="opacity-slider">
            <span className="opacity-slider-label">
              {t('customize.bg_dim_label')}
              <span className="opacity-slider-value">
                {Math.round((theme.backgroundOpacity ?? 0) * 100)}%
              </span>
            </span>
            <input
              type="range"
              min="0"
              max="0.9"
              step="0.05"
              value={theme.backgroundOpacity ?? 0.55}
              onChange={handleOpacityChange}
            />
            <span className="opacity-slider-help">{t('customize.bg_dim_hint')}</span>
          </label>
        )}
      </section>

      {/* ── Live preview ─────────────────────────────────────── */}
      <section className="customize-section">
        <h2 className="customize-section-title">
          <Eye size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          {t('customize.preview_title')}
        </h2>

        <div className="theme-preview">
          <div className="theme-preview-card">
            <p className="theme-preview-label">{t('customize.preview_workout')}</p>
            <p className="theme-preview-title">Push Day 1</p>
            <div className="theme-preview-row">
              <span className="theme-preview-stat">
                <strong>5</strong> {t('customize.preview_exercises')}
              </span>
              <span className="theme-preview-stat">
                <strong>18</strong> {t('customize.preview_sets')}
              </span>
            </div>
            <div className="theme-preview-buttons">
              <button type="button" className="btn btn-primary btn-sm">
                {t('customize.preview_start')}
              </button>
              <button type="button" className="btn btn-outline btn-sm">
                {t('customize.preview_view')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Reset ────────────────────────────────────────────── */}
      <button
        type="button"
        className="btn btn-secondary w-full"
        style={{ marginTop: 8 }}
        onClick={reset}
      >
        <RotateCcw size={16} />
        {t('customize.reset_btn')}
      </button>
    </div>
  );
};

export default CustomizePage;
